<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { api } from "@/api";
import { parseCompId } from "@shared/comp";
import type { LeaderboardsResponse } from "@shared/types";
import HeaderStrip from "@/components/leaderboard/HeaderStrip.vue";
import SidePrizes from "@/components/leaderboard/SidePrizes.vue";
import MainTable from "@/components/leaderboard/MainTable.vue";
import MobileCards from "@/components/leaderboard/MobileCards.vue";

const data = ref<LeaderboardsResponse | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

const route = useRoute();
const compId = parseCompId(route.query.comp);

// Comp 2 (family) drops the Killearn FC branding, so swap the favicon for a
// neutral football emoji rather than the club badge baked into index.html.
function useFootballFavicon() {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<text y=".9em" font-size="90">⚽</text></svg>';
  const href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"], link[rel="apple-touch-icon"]')
    .forEach(link => { link.href = href; });
}

if (compId === 2) useFootballFavicon();

onMounted(async () => {
  try {
    data.value = await api.leaderboards(compId);
  } catch (e) {
    error.value = e instanceof Error ? e.message : String(e);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <main class="max-w-5xl mx-auto px-4 py-6">
    <p v-if="loading" class="text-slate-500">Loading…</p>
    <p v-else-if="error" class="text-red-600">Failed to load: {{ error }}</p>
    <template v-else-if="data">
      <HeaderStrip
        :entry-count="data.meta.entryCount"
        :days-until-kickoff="data.meta.daysUntilKickoff"
        :comp-id="compId"
      />
      <SidePrizes :prizes="data.sidePrizes" :comp-id="compId" />
      <MainTable :entries="data.entries" />
      <MobileCards :entries="data.entries" />
    </template>
  </main>
</template>
